import React, { Fragment, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Button, Card, CardBody, CardTitle, Col, FormGroup, Label, Row } from "reactstrap";
import { AvCheckbox, AvCheckboxGroup, AvForm } from "availity-reactstrap-validation";
import { Link } from "react-router-dom";

import formsValidators, {
  requiredIfOtherExist,
} from "../../validators/form.validators";
import useDropzoneFacade from "../../hooks/useDropzoneFacade";
import useAvForm from "../../hooks/useAvForm";
import FormGroupText from "./inputs/FormGroupText";
import WYSISYGComponent from "./inputs/WYSISYGComponent";
import FormGroupReactSelect from "./inputs/FormGroupReactSelect";
import noop from "../../helpers/noop";
import useMountedState from "../../hooks/useMountedState";
import FormGroupImages from "./inputs/FormGroupImages";
import InputWithReadOnlyTextPart from "./inputs/InputWithReadOnlyTextPart";
import useInputWithReadOnlyTextPart from "../../hooks/useInputWithReadOnlyTextPart";
import getReadOnlyUrlPart from "../../helpers/getReadOnlyUrlPart";
import {
  getCoursesForOptionForReferralSource,
} from "../../store/dashboard/ReferralSources/actions";

import styles from "./forms.module.scss";
import FormGroupMultiSelect from './inputs/FormGroupMultiSelect';
import { MAX_TAG_LENGTH } from '../../constants/leghtValidity';
import { VALUE_FIELD_ID } from '../../constants/selectConstants';

const currencies = ["EUR", "USD", "GBP", "NZD", "AUD", "CAD", "INR"];
const applicableToSubscriptionValue = 'ApplicableToSubscription';

const initialState = {
  title: "",
  code: "",
  courses: "",
  heading: "",
  description: "",
};

const SaveReferralSource = ({
  model,
  saveReferralSourceFunc,
  referralSources,
  updateDirtyState = noop,
  getCoursesForOptionForReferralSource,
}) => {
  const [formData, updateFormData] = useState(initialState);
  // const [initialFile, updateInitialFile] = useMountedState(null);
  const { loading, dataCoursesForOption: coursesData } = referralSources;
  const [referralSourceThumbnailImageInitialFile, updateReferralSourceThumbnailImageInitialFile] = useMountedState(null);

  const [ready, updateReadyState] = useMountedState(false);
  const [formRef, hasFormError, handleFormChange, isDirty, values] =
    useAvForm();
    // const [
    //   error,
    //   selectedFiles,
    //   handleAcceptedFiles,
    //   setSelectedFiles,
    // ] = useDropzoneFacade();

  const code = useInputWithReadOnlyTextPart(
    values?.title,
    formRef,
    model?.code
  );

  // console.log('Courses: ', model.courses);

  useEffect(() => {
    getCoursesForOptionForReferralSource({});
  }, []);

  const [
    referralSourceThumbnailImageError,
    referralSourceThumbnailImageSelectedFiles,
    referralSourceThumbnailImageHandleAcceptedFiles,
    setReferralSourceThumbnailImageSelectedFiles,
  ] = useDropzoneFacade();

  const handleFormSubmit = (event, values) => {
    const {
      title,
      code,
      courses,
      heading,
      description,
    } = values;
    const model = {
      title,
      code,
      courseIds: courses,
      heading,
      description,
    };

    if (referralSourceThumbnailImageInitialFile !== referralSourceThumbnailImageSelectedFiles[0]) {
      model.referralSourceThumbnailImage = referralSourceThumbnailImageSelectedFiles;
    }

    saveReferralSourceFunc(model);
  };

  const pageTitle = useMemo(
    () => (model ? "Edit referral source" : "Create referral source"),
    [model]
  );
  const btnSaveLabel = useMemo(
    () => (model ? "Save" : "Create"),
    [model]
  );
  const disableState = useMemo(
    () =>
      !(
        isDirty ||
        referralSourceThumbnailImageInitialFile !== referralSourceThumbnailImageSelectedFiles[0] 
      ) ||
      loading ||
      referralSourceThumbnailImageSelectedFiles.length === 0 ||
      hasFormError,
    [
      loading,
      hasFormError,
      isDirty,
      model,
      referralSourceThumbnailImageInitialFile,
      referralSourceThumbnailImageSelectedFiles,
    ]
  );

  useEffect(() => {
    updateDirtyState(isDirty);
  }, [isDirty]);

  useEffect(() => {
    if (!!model) {
      const {
        title,
        courses,
        heading,
        description,
        referralSourceThumbnailImageInitialFile,
      } = model;
      const formData = {
        ...initialState,
        title,
        courses,
        heading,
        description,
      };

      if (referralSourceThumbnailImageInitialFile) {
        updateReferralSourceThumbnailImageInitialFile(referralSourceThumbnailImageInitialFile);
        referralSourceThumbnailImageHandleAcceptedFiles([referralSourceThumbnailImageInitialFile], []);
      }

      updateFormData(formData);
    }

    updateReadyState(true);
  }, [model]);

  useEffect(() => {
    if (ready && referralSourceThumbnailImageSelectedFiles.length === 0 && referralSourceThumbnailImageInitialFile) {
      setReferralSourceThumbnailImageSelectedFiles([referralSourceThumbnailImageInitialFile]);
    }
  }, [referralSourceThumbnailImageSelectedFiles]);

  const getCoursesValue = useMemo(() => {
    if (!!model) {
      return model.courses.map(({ id, title }) => {
        return {
          id: id,
          name: title,
        }
      });
    }
  }, [model]);

  return (
    ready && (
      <>
        <AvForm
          onValidSubmit={handleFormSubmit}
          ref={formRef}
          model={formData}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              return false;
            }
          }}
        >
          <Card>
            <CardBody>
              <CardTitle className="mb-4">{pageTitle}</CardTitle>
              <FormGroupText
                name="title"
                label="Referral Source title"
                placeholder="Enter referral source title"
                validator={formsValidators.course.title}
                handleFormChange={handleFormChange}
              />
              <InputWithReadOnlyTextPart
                name="code"
                label="Referral Source"
                readOnlyPart={''}
                validator={formsValidators.course.urlPart}
                handleFormChange={handleFormChange}
                value={code}
              />
              <FormGroupImages
                multiple={false}
                label="Referral Source Thumbnail"
                error={referralSourceThumbnailImageError}
                selectedImages={referralSourceThumbnailImageSelectedFiles}
                initialFile={referralSourceThumbnailImageInitialFile}
                handleAcceptedImages={referralSourceThumbnailImageHandleAcceptedFiles}
                setSelectedImages={setReferralSourceThumbnailImageSelectedFiles}
             />
              <FormGroupText
                name="heading"
                label="Referral Source Heading"
                placeholder="Enter referral source heading"
                validator={formsValidators.referralSource.heading}
                handleFormChange={handleFormChange}
              />

              <WYSISYGComponent
                name="description"
                label="Description"
                validator={formsValidators.course.overview}
                handleFormChange={handleFormChange}
              />
              <FormGroupMultiSelect
                name="courses"
                allowCreate={false}
                label="Courses"
                placeholder="Select courses"
                value={getCoursesValue}
                options={coursesData.map(({ id, title }) => ({
                  id: id,
                  name: title,
                }))}
                validator={formsValidators.bundle.courses}
                handleFormChange={handleFormChange}
                formState={formData}
                updateFormState={updateFormData}
                maxCreateLength={MAX_TAG_LENGTH}
                isMulti
                optionValueName={VALUE_FIELD_ID}
            />
            </CardBody>
          </Card>
          <Row className="justify-content-end">
            <Col xs="12">
              <Link
                to={`/referral-sources`}
              >
                <Button color="secondary" className="mr-2">
                  Cancel
                </Button>
              </Link>
              <Button
                type="submit"
                color="primary"
                disabled={disableState}
                className={styles.btnSave}
              >
                {btnSaveLabel}
              </Button>
            </Col>
          </Row>
        </AvForm>
      </>
    )
  );
};

const mapStateToProps = ({ ReferralSources, Courses }) => {
  return {
    referralSources: ReferralSources,
    courses: Courses,
  };
};

export default connect(mapStateToProps, { getCoursesForOptionForReferralSource })(SaveReferralSource);
